<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { ProductPickingStatus } from '@/components/chips/PickingStatusChip.vue';
import type { HeadersData } from '@/composables/useTable';

import { computed } from 'vue';

import PickingStatusChip from '@/components/chips/PickingStatusChip.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
// import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import useTable from '@/composables/useTable';
import * as commonConstants from '@/constants/common';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  id: number;
  model: string;
  serialNumber: string;
  description: string;
  quantId?: string | null;
  quantity: string;
  scannedQty: string;
  type: string;
  pickingStatus: ProductPickingStatus;
}

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<DataItem[]>;
  isStaff?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
  isStaff: false,
});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<DataItem[]>>
>;

// Picking confirmation is internal warehouse state, not customer-facing.
const staffOnlyColumns = ['scannedQty', 'pickingStatus'];

const headersData = computed<HeadersData>(function () {
  const headers = [
    {
      value: 'model',
      title: 'Model',
      isNotSortable: true,
    },
    {
      value: 'serialNumber',
      title: 'Serial number',
      isNotSortable: true,
    },
    {
      value: 'description',
      title: 'Description',
      isNotSortable: true,
    },
    {
      value: 'quantId',
      title: 'Quant ID',
      isNotSortable: true,
    },
    {
      value: 'quantity',
      title: 'Quantity',
      isNotSortable: true,
    },
    {
      value: 'scannedQty',
      title: 'Scanned Qty',
      isNotSortable: true,
    },
    {
      value: 'type',
      title: 'Type',
      isNotSortable: true,
    },
    {
      value: 'pickingStatus',
      title: 'Status',
      isNotSortable: true,
    },
  ];
  if (props.isStaff) return headers;
  return headers.filter((header) => !staffOnlyColumns.includes(header.value));
});

const foldColspan = computed(() => {
  return headersData.value.length;
});

/** Returns a link to the Quant page in Odoo WH */
function warehouseLink(quantId: string): string {
  return `${import.meta.env.VITE_APP_URL || ''}/web#id=${quantId}&view_type=form&model=stock.quant&action=176`;
}
</script>

<template>
  <table
    :class="[tableStyles.table, $style.detailedJobProductsTable]"
    data-testid="detailedJobProductsTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.id">
        <tr>
          <td :class="$style.modelTd">
            {{ dataItem.model }}
          </td>
          <td :class="$style.serialNumberTd">
            {{ dataItem.serialNumber }}
          </td>
          <td :class="$style.descriptionTd">
            {{ dataItem.description }}
          </td>
          <td :class="$style.quantIdTd">
            <template v-if="dataItem.quantId != null">
              <a :href="warehouseLink(dataItem.quantId)" target="_blank">{{
                dataItem.quantId
              }}</a>
            </template>
            <template v-else>{{ commonConstants.EM_DASH }}</template>
          </td>
          <td :class="$style.quantityTd">
            {{ dataItem.quantity }}
          </td>
          <td v-if="props.isStaff" :class="$style.scannedQtyTd">
            {{ dataItem.scannedQty }}
          </td>
          <td :class="$style.typeTd">
            {{ dataItem.type }}
          </td>
          <td v-if="props.isStaff" :class="$style.pickingStatusTd">
            <PickingStatusChip :status="dataItem.pickingStatus" />
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobProductsTable {
}
  .modelTh {
  }
  .serialNumberTh {
  }
  .descriptionTh {
  }
  .quantIdTh {
    white-space: nowrap;
  }
  .quantityTh {
  }
  .scannedQtyTh {
    white-space: nowrap;
  }
  .typeTh {
  }
  .pickingStatusTh {
  }
  .modelTd {
  }
  .serialNumberTd {
  }
  .descriptionTd {
  }
  .quantIdTd {
  }
  .quantityTd {
  }
  .scannedQtyTd {
  }
  .typeTd {
  }
  .pickingStatusTd {
  }
</style>
